import React, { useState } from 'react'
import { useHistory } from 'react-router'
import moment from 'moment'
import { useMutation, useQuery } from '@apollo/client'
import { ButtonBase, IconButton } from '@mui/material'
import { AutoAwesomeOutlined } from '@mui/icons-material'
import { colors, Snackbar, Tooltip } from '@resiliatech/birdsong'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import UPDATE_GOAL from 'Apollo/Mutations/UpdateGoal'
import GET_FUNDER_ORG_COHORT from 'Apollo/Queries/GetFunderOrgCohort'
import { GOALS, GOALS_SIMPLE } from 'Apollo/Queries/Goals'
import { useAppState } from 'Context/AppContext/AppContext'
import { Goal, GoalType, GoalUpdateInput } from 'Generated/graphql-types'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { analytics } from 'Modules/Analytics'
import getEnvironment from 'Modules/handleEnvironment'
import { categoriesIncludingOld, impactCategories } from 'TypeDefs/Goal'
import { GoalProgress } from './GoalProgress'
import {
  AvatarInformation,
  AvatarTooltip,
  Categories,
  Category,
  CategoryAndStatus,
  ChipContainer,
  CompleteButton,
  Date as DateLabel,
  DateInfo,
  Details,
  EditOptions,
  GoalCardContainer,
  GoalDescription,
  GoalTitle,
  LeftChipsContainer,
  Owner,
  OwnerAndDates,
  RightChipsContainer,
  TopSection,
} from './Goals.styled'
import { GOALS_PATH } from '../types'
import { GoalModal } from '../GoalModal/GoalModal'
import { DeleteGoalModal } from '../GoalModal/DeleteGoalModal'
import { TaskCompletionChip } from '../Chips/TaskCompletionChip'
import { NonprofitOrgAvatars } from './NonprofitOrgsAvatars'
import { FUNDER_IMPACT_GOALS_PATH } from '../../GoalPage/utils'
import { UnassignedTasksChip } from '../Chips/UnassignedTasksChip'
import { CampaignGoalChip } from '../Chips/CampaignGoalChip'
import { CollaborativeGoalChip } from '../Chips/CollaborativeGoalChip'
import { checkCanGoalBeCompleted, getFunderCount } from './utils'
import { GoalCompletionChip } from '../Chips/GoalCompletionChip'

type GoalCardProps = {
  goal: Goal
  hasLinkedCampaign: boolean
  isInteractive?: boolean
  setPreSelectedGoal?: (goal: Goal) => void
}

const GOAL_DATE_FORMAT = 'MM-DD-YYYY'
const IMPACT_GOALS_PATH = '/impact/goals'

const iconStyles = {
  color: colors.interactive.primaryEnabled,
  cursor: 'pointer ',
  fontSize: 'large',
  marginRight: '10px',
  verticalAlign: 'middle',
}

const iconColorStyles = { color: '#706666' }

export const GoalCard = ({
  goal,
  hasLinkedCampaign,
  isInteractive = true,
  setPreSelectedGoal,
}: GoalCardProps) => {
  const { teamMap = {} } = useNonprofitOrgTeam()
  const {
    userConfig: { id, permissions },
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      role,
      platformPlan,
      activeFunders,
    },
  } = useAppState()
  const history = useHistory()

  // TODO: This can be deleted when product is done demoing to initial funders feature was made for
  const currentEnv = getEnvironment()
  const isLocalOrStaging = currentEnv === 'local' || currentEnv === 'staging'
  const canEditAndDeleteFunderGoalsForDemoPurposes =
    permissions?.includes('page:internal:self_serve') && isLocalOrStaging
  // END TODO

  const [showEditGoalModal, setShowEditGoalModal] = useState(false)
  const [showDeleteGoalModal, setShowDeleteGoalModal] = useState(false)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const isCampaignGoal = goal.type === GoalType.Campaign
  const campaignChipText = hasLinkedCampaign
    ? 'Connected to Resilia Campaigns'
    : 'Disconnected from Resilia Campaigns'

  const [updateGoal] = useMutation(UPDATE_GOAL, {
    refetchQueries: [
      { query: GOALS, variables: { activeOnly: true, nonprofitOrgId } },
      {
        query: GOALS_SIMPLE,
        variables: {
          activeOnly: true,
          includeOnboarding: true,
          nonprofitOrgId,
        },
      },
    ],
  })

  const { data: { funderOrgCohortQuery: { nonprofitOrgs = [] } = {} } = {} } =
    useQuery(GET_FUNDER_ORG_COHORT, {
      variables: {
        nonprofitOrgIds: [nonprofitOrgId],
        returnAllNonprofitsInCohort: true,
      },
      skip: activeFunders.length === 0,
    })

  const displayMarkCompleteFunctions = checkCanGoalBeCompleted(goal)

  if (!goal) return null

  const goalCategory =
    goal.category && categoriesIncludingOld[goal.category]
      ? categoriesIncludingOld[goal.category]
      : ''
  const funderSharedGoal = goal.FunderOrgToSharedGoal.filter(
    (sharedGoal) => sharedGoal.goalId === goal.id && !sharedGoal.archivedAt
  )[0]
  const fundersSharedGoal = goal.FunderOrgToSharedGoal
  const funderCountForSharedGoal = getFunderCount(fundersSharedGoal)
  const sharedWithFunderIds = goal.FunderOrgToSharedGoal.map(
    (sharedGoal) => sharedGoal.funderOrgId
  )

  const isGoalSharedByFunder =
    funderSharedGoal && funderSharedGoal.isGoalSharedByFunder

  const isGoalSharedByNonprofit =
    funderSharedGoal && funderSharedGoal.isGoalSharedByNonprofit

  const dispatchGoalCardClicked = () => {
    analytics.trackActionCreator(goals.View_Goal_Details_Clicked)({
      goalId: goal.id,
      userId: id,
      goalType: goal.type,
      isGoalSharedWithFunder: !!funderSharedGoal,
    })
  }

  const dispatchEditGoalClicked = () => {
    analytics.trackActionCreator(initiatives.Edit_Goal_Clicked)({
      goalId: goal.id,
      goalType: goal.type,
      templateLibraryId: null,
    })
  }

  const handleRouteToGoal = () => {
    const isFunderImpactPath = history.location.pathname.includes(
      FUNDER_IMPACT_GOALS_PATH
    )
    const goalPath = `${GOALS_PATH}/${goal.id}?section=tasks`
    const funderImpactGoalPath = `${FUNDER_IMPACT_GOALS_PATH}/${goal.id}`

    if (!isFunderImpactPath) {
      history.push(goalPath)
    } else {
      history.push(funderImpactGoalPath)
    }
    dispatchGoalCardClicked()
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowEditGoalModal(true)
    dispatchEditGoalClicked()
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteGoalModal(true)
  }

  const handleMagicClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (setPreSelectedGoal) {
      setPreSelectedGoal(goal)
    }

    analytics.trackActionCreator(initiatives.Create_Ai_Story_Clicked)({
      userId: id,
      goalId: goal.id,
      goalType: goal.type,
      templateLibraryId: goal.templateLibraryId,
    })
  }

  const handleMarkComplete = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleUpdateGoal({
      id: goal.id,
      isComplete: !goal.isComplete,
      shareWithFunderOrgIds: sharedWithFunderIds,
      ownerId: goal.owner?.id,
    })
  }

  const popToast = (message: string) => {
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  const handleUpdateGoal = async (goalUpdateInfo: GoalUpdateInput) => {
    const { data } = await updateGoal({
      variables: { goalUpdateInfo },
    })

    setShowEditGoalModal(false)
    if (data?.updateGoal.id) popToast('Your Goal has been updated')
    else {
      popToast('Failed to update Goal')
    }
  }

  const handleDeleteGoal = async (goalId: number) => {
    const archiveInput = { id: goalId, archivedAt: new Date() }
    const { data } = await updateGoal({
      variables: { goalUpdateInfo: archiveInput },
    })
    setShowDeleteGoalModal(false)
    analytics.trackActionCreator(goals.Delete_Goal_Submitted)({
      goalId: goal.id,
      userAccountType: role,
      platformPlan,
    })
    if (data?.updateGoal.id) {
      history.push(IMPACT_GOALS_PATH, { toastToDisplay: 'deleteGoal' })
    } else {
      popToast('Failed to delete Goal')
    }
  }

  const numTasksIncomplete = goal.ChecklistItem.filter(
    (task) => !task.isCompleted
  ).length
  const numTasksTotal = goal.ChecklistItem.length
  const numTasksComplete = numTasksTotal - numTasksIncomplete
  const numTasksUnassigned = goal.ChecklistItem.filter(
    (task) => !task?.owner?.id
  ).length

  const taskStatusPercentage = (numTasksComplete / numTasksTotal) * 100
  const taskStatusIndicator =
    taskStatusPercentage === 100 ? (
      <CheckCircleOutlineOutlinedIcon />
    ) : (
      <GoalProgress value={taskStatusPercentage} showLabel={false} size={20} />
    )

  const BaseContainer = isInteractive ? ButtonBase : React.Fragment
  const hasTasks = numTasksTotal > 0
  const showAiButton = !!setPreSelectedGoal

  const categories = [
    ...(goal.impactCategory ? [impactCategories[goal.impactCategory]] : []),
    ...(goalCategory && goalCategory !== 'None' ? [goalCategory] : []),
  ]

  return (
    <>
      <BaseContainer>
        <GoalCardContainer
          onClick={isInteractive ? handleRouteToGoal : undefined}
          isInteractive={isInteractive}
        >
          <Details>
            <TopSection>
              <CategoryAndStatus>
                {categories.length > 0 && (
                  <Categories>
                    {categories.map((category) => (
                      <Category key={category}>{category}</Category>
                    ))}
                  </Categories>
                )}
                {!isGoalSharedByFunder && (
                  <EditOptions>
                    <EditOutlinedIcon
                      onClick={handleEditClick}
                      sx={iconStyles}
                    />
                    <DeleteOutlinedIcon
                      onClick={handleDeleteClick}
                      sx={iconStyles}
                    />
                    {showAiButton && (
                      <Tooltip
                        title='Create story with AI'
                        arrow
                        PopperProps={{
                          placement: 'left',
                        }}
                      >
                        <AutoAwesomeOutlined
                          onClick={handleMagicClick}
                          data-testid='aiStoryButton'
                          sx={{ ...iconStyles, color: '#4D3D99' }}
                        />
                      </Tooltip>
                    )}
                    {displayMarkCompleteFunctions && (
                      <CompleteButton
                        variant='outlined'
                        onClick={handleMarkComplete}
                      >
                        {goal.isComplete
                          ? 'Mark as Incomplete'
                          : 'Mark as Complete'}
                      </CompleteButton>
                    )}
                  </EditOptions>
                )}
                {isGoalSharedByFunder && showAiButton && (
                  <EditOptions>
                    <Tooltip
                      title='Create story with AI'
                      arrow
                      PopperProps={{
                        placement: 'left',
                      }}
                    >
                      <AutoAwesomeOutlined
                        onClick={handleMagicClick}
                        data-testid='aiStoryButton'
                        sx={{ ...iconStyles, color: '#4D3D99' }}
                      />
                    </Tooltip>
                  </EditOptions>
                )}
                {isGoalSharedByFunder &&
                  canEditAndDeleteFunderGoalsForDemoPurposes && (
                    <AvatarTooltip
                      title='These actions for funder-shared goals are only shown to certain internal users on local and staging environments.'
                      arrow
                      PopperProps={{
                        placement: 'left',
                      }}
                    >
                      <EditOptions>
                        <EditOutlinedIcon
                          onClick={handleEditClick}
                          sx={iconStyles}
                        />
                        <DeleteOutlinedIcon
                          onClick={handleDeleteClick}
                          sx={iconStyles}
                        />
                      </EditOptions>
                    </AvatarTooltip>
                  )}
              </CategoryAndStatus>
              {goal.title && (
                <GoalTitle data-testid='goalTitle' variant='h5'>
                  {goal.title}
                </GoalTitle>
              )}
              <OwnerAndDates>
                {!funderSharedGoal && (
                  <Owner>
                    <PersonOutlineOutlinedIcon sx={{ fontSize: '16px' }} />
                    {goal?.owner && teamMap[goal.owner.id]
                      ? `${goal.owner.firstName} ${goal.owner.lastName}`
                      : 'Not Assigned'}
                  </Owner>
                )}
                <DateInfo>
                  <DateLabel variant='caption'>
                    Start Date:{' '}
                    {goal.startDate
                      ? moment.utc(goal.startDate).format(GOAL_DATE_FORMAT)
                      : 'Not Specified'}
                  </DateLabel>
                  <DateLabel variant='caption'>
                    Due Date:{' '}
                    {goal.endDate
                      ? moment.utc(goal.endDate).format(GOAL_DATE_FORMAT)
                      : 'Not Specified'}
                  </DateLabel>
                </DateInfo>
              </OwnerAndDates>
            </TopSection>
            {goal.description && (
              <GoalDescription variant='body1'>
                {goal.description}
              </GoalDescription>
            )}
            <ChipContainer>
              <LeftChipsContainer>
                {hasTasks && (
                  <>
                    <TaskCompletionChip
                      numTasksComplete={numTasksComplete}
                      numTasksTotal={numTasksTotal}
                      taskStatusIndicator={taskStatusIndicator}
                      taskStatusPercentage={taskStatusPercentage}
                    />
                    <UnassignedTasksChip
                      iconColorStyles={iconColorStyles}
                      numTasksUnassigned={numTasksUnassigned}
                    />
                  </>
                )}
                {isCampaignGoal && (
                  <CampaignGoalChip campaignChipText={campaignChipText} />
                )}
                {goal.isComplete && (
                  <GoalCompletionChip iconColorStyles={iconColorStyles} />
                )}
              </LeftChipsContainer>
              <RightChipsContainer>
                {isGoalSharedByFunder && (
                  <CollaborativeGoalChip
                    direction='from'
                    funderSharedGoalName={
                      funderSharedGoal?.funderOrg?.name || ''
                    }
                    iconColorStyles={iconColorStyles}
                  />
                )}
                {isGoalSharedByNonprofit && (
                  <CollaborativeGoalChip
                    direction='with'
                    funderSharedGoalName={
                      funderSharedGoal?.funderOrg?.name || ''
                    }
                    funderCount={funderCountForSharedGoal}
                    iconColorStyles={iconColorStyles}
                  />
                )}
                {!!nonprofitOrgs.length && isGoalSharedByFunder && (
                  <AvatarTooltip
                    title='You and other nonprofits are joining forces with your funder to achieve this important goal together. Collaborating is key to make community impact, we’re thrilled to have you on board.'
                    arrow
                    PopperProps={{
                      placement: 'left',
                    }}
                  >
                    <AvatarInformation>
                      <NonprofitOrgAvatars funderOrgCohort={nonprofitOrgs} />
                    </AvatarInformation>
                  </AvatarTooltip>
                )}
              </RightChipsContainer>
            </ChipContainer>
          </Details>
        </GoalCardContainer>
      </BaseContainer>
      {showEditGoalModal && (
        <GoalModal
          isOpen={showEditGoalModal}
          onClose={() => setShowEditGoalModal(false)}
          goal={goal}
          onUpdateGoal={handleUpdateGoal}
        />
      )}
      {showDeleteGoalModal && (
        <DeleteGoalModal
          isOpen={showDeleteGoalModal}
          onClose={() => setShowDeleteGoalModal(false)}
          onDeleteGoal={handleDeleteGoal}
          goalId={goal.id}
          isFunderSharedGoal={!!funderSharedGoal}
        />
      )}
      <Snackbar
        open={showSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={snackbarMessage}
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        action={
          <IconButton
            aria-label='close'
            color='inherit'
            sx={{ p: 0.5 }}
            onClick={() => setShowSnackbar(false)}
          >
            <CloseIcon />
          </IconButton>
        }
      />
    </>
  )
}
